import { DateTime } from 'luxon'
import { HOUR_MS } from '~/utils/date'
import type { TRPCInputs } from '~/utils/trpc'
import { toMillis, type LeakV0ChartModel } from './chartModel'
import type { StationLeakV0Episode } from './useLeakV0Episodes'

export type LeakV0Window = Pick<TRPCInputs['ops']['stations']['leakV0Series'], 'from' | 'to'>
export type LeakV0MsRange = Pick<LeakV0ChartModel, 'fromMs' | 'toMs'>

export const WINDOW_DAYS = 14
export const MIN_ZOOM_HOURS = 6

const STEP_DAYS = 7
const EPISODE_LEAD_DAYS = 1
const MIN_ZOOM_MS = MIN_ZOOM_HOURS * HOUR_MS

// Both ends always come off one anchor by a single calendar step. Shifting them independently
// measures 14.04 days across a DST transition by luxon's reckoning, which the procedure rejects.
const windowEndingAt = (to: DateTime<true>): LeakV0Window => ({
  from: to.minus({ days: WINDOW_DAYS }).toISO(),
  to: to.toISO()
})

const windowStartingAt = (from: DateTime<true>): LeakV0Window => ({
  from: from.toISO(),
  to: from.plus({ days: WINDOW_DAYS }).toISO()
})

const parseInstant = (iso: string): DateTime<true> | null => {
  const at = DateTime.fromISO(iso)
  return at.isValid ? at : null
}

export const defaultWindow = (): LeakV0Window => windowEndingAt(DateTime.now())

/** One week per step, clamped to the present so forward lands on the last 14 days, never past it. */
export const shiftWindow = (current: LeakV0Window, direction: -1 | 1): LeakV0Window => {
  const to = parseInstant(current.to)
  if (to === null) return current

  const shifted = to.plus({ days: direction * STEP_DAYS })
  const now = DateTime.now()
  return windowEndingAt(shifted.toMillis() > now.toMillis() ? now : shifted)
}

// The detector ticks hourly, so a window already ending inside the last hour has nothing ahead of it.
export const canShiftForward = (current: LeakV0Window): boolean => toMillis(current.to) < Date.now() - HOUR_MS

/**
 * §11.1: one rule regardless of episode length. The day of lead keeps onset off the left edge and
 * shows the pre-onset baseline; a longer episode is walked forward with the arrows.
 */
export const windowForEpisode = (onsetAt: string): LeakV0Window | null => {
  const onset = parseInstant(onsetAt)
  return onset === null ? null : windowStartingAt(onset.minus({ days: EPISODE_LEAD_DAYS }))
}

/** Overlap, not containment: zooming into an episode must not drop it, and an open one never ends. */
export const episodesInWindow = (
  episodes: StationLeakV0Episode[],
  { from, to }: LeakV0Window
): StationLeakV0Episode[] => {
  const fromMs = toMillis(from)
  const toMs = toMillis(to)

  return episodes.filter(
    (episode) =>
      toMillis(episode.onsetAt) <= toMs && (episode.clearedAt === null || toMillis(episode.clearedAt) >= fromMs)
  )
}

/**
 * A drag under the minimum widens around its own centre rather than being ignored, so a twitchy
 * selection still lands somewhere deliberate. Never leaves `bounds`, and never narrows below 6h.
 */
export const zoomWindow = (anchorMs: number, releaseMs: number, bounds: LeakV0MsRange): LeakV0Window => {
  const spanMs = Math.min(Math.max(Math.abs(releaseMs - anchorMs), MIN_ZOOM_MS), bounds.toMs - bounds.fromMs)
  const centreMs = (anchorMs + releaseMs) / 2
  const fromMs = Math.min(Math.max(centreMs - spanMs / 2, bounds.fromMs), bounds.toMs - spanMs)

  return { from: new Date(fromMs).toISOString(), to: new Date(fromMs + spanMs).toISOString() }
}
